/**
 * Centralized Status Color System (UX-13)
 *
 * Provides consistent color mappings for all status types throughout the application.
 * Uses semantic badge variants from the Badge component for WCAG AA compliance.
 *
 * Usage:
 * import { statusColors, getStatusColor } from '@/lib/theme-colors'
 * <Badge variant={statusColors.success}>Completed</Badge>
 */

/**
 * Status Color Constants
 * Maps semantic status types to badge variants
 */
export const statusColors = {
  // Success states - green
  success: "success" as const,
  completed: "success" as const,
  paid: "success" as const,
  accepted: "success" as const,
  active: "success" as const,

  // Warning states - amber/yellow
  warning: "warning" as const,
  pending: "warning" as const,
  expired: "warning" as const,
  overdue: "warning" as const,

  // Error/Failed states - red
  error: "error" as const,
  failed: "error" as const,
  rejected: "destructive" as const,
  cancelled: "destructive" as const,

  // Info/In Progress states - blue
  info: "info" as const,
  inProgress: "info" as const,
  sent: "info" as const,
  viewed: "info" as const,

  // Neutral/Draft states - gray
  neutral: "secondary" as const,
  draft: "secondary" as const,
  onHold: "secondary" as const,
  default: "default" as const,
} as const;

/**
 * Job Status Color Mapping
 */
export const jobStatusColors = {
  PENDING: statusColors.pending,
  IN_PROGRESS: statusColors.inProgress,
  ON_HOLD: statusColors.onHold,
  COMPLETED: statusColors.completed,
  CANCELLED: statusColors.cancelled,
} as const;

/**
 * Quote Status Color Mapping
 */
export const quoteStatusColors = {
  DRAFT: statusColors.draft,
  SENT: statusColors.sent,
  ACCEPTED: statusColors.accepted,
  REJECTED: statusColors.rejected,
  EXPIRED: statusColors.expired,
} as const;

/**
 * Invoice Status Color Mapping
 */
export const invoiceStatusColors = {
  DRAFT: statusColors.draft,
  SENT: statusColors.sent,
  VIEWED: statusColors.viewed,
  PAID: statusColors.paid,
  OVERDUE: statusColors.overdue,
  CANCELLED: statusColors.cancelled,
} as const;

/**
 * Email Status Color Mapping
 */
export const emailStatusColors = {
  DRAFT: statusColors.draft,
  SENT: statusColors.sent,
  DELIVERED: statusColors.success,
  OPENED: statusColors.info,
  CLICKED: statusColors.success,
  BOUNCED: statusColors.error,
  FAILED: statusColors.failed,
} as const;

/**
 * Time Off Request Status Color Mapping
 */
export const timeOffStatusColors = {
  PENDING: statusColors.pending,
  APPROVED: statusColors.success,
  REJECTED: statusColors.rejected,
  CANCELLED: statusColors.cancelled,
} as const;

/**
 * Generic status color getter
 * Converts any status string to appropriate badge variant
 */
export function getStatusColor(
  status: string,
  type?: "job" | "quote" | "invoice" | "email" | "timeOff"
): "default" | "secondary" | "success" | "warning" | "destructive" | "info" | "error" {
  // If type is specified, use the specific mapping
  if (type === "job" && status in jobStatusColors) {
    return jobStatusColors[status as keyof typeof jobStatusColors];
  }
  if (type === "quote" && status in quoteStatusColors) {
    return quoteStatusColors[status as keyof typeof quoteStatusColors];
  }
  if (type === "invoice" && status in invoiceStatusColors) {
    return invoiceStatusColors[status as keyof typeof invoiceStatusColors];
  }
  if (type === "email" && status in emailStatusColors) {
    return emailStatusColors[status as keyof typeof emailStatusColors];
  }
  if (type === "timeOff" && status in timeOffStatusColors) {
    return timeOffStatusColors[status as keyof typeof timeOffStatusColors];
  }

  // Generic fallback based on status name
  const normalizedStatus = status.toLowerCase().replace(/_/g, "");

  // Success states
  if (["completed", "success", "paid", "accepted", "approved", "active"].includes(normalizedStatus)) {
    return statusColors.success;
  }

  // Warning states
  if (["pending", "warning", "expired", "overdue"].includes(normalizedStatus)) {
    return statusColors.warning;
  }

  // Error/Failed states
  if (["error", "failed", "rejected", "cancelled"].includes(normalizedStatus)) {
    return statusColors.error;
  }

  // Info/In Progress states
  if (["inprogress", "info", "sent", "viewed", "delivered", "opened"].includes(normalizedStatus)) {
    return statusColors.info;
  }

  // Neutral/Draft states
  if (["draft", "onhold", "neutral"].includes(normalizedStatus)) {
    return statusColors.neutral;
  }

  return statusColors.default;
}

/**
 * Get quote status color (legacy compatibility)
 */
export function getQuoteStatusColor(
  status: "DRAFT" | "SENT" | "ACCEPTED" | "REJECTED" | "EXPIRED"
): "default" | "secondary" | "success" | "warning" | "destructive" | "info" {
  return quoteStatusColors[status];
}

/**
 * Get invoice status color (legacy compatibility)
 */
export function getInvoiceStatusColor(
  status: "DRAFT" | "SENT" | "VIEWED" | "PAID" | "OVERDUE" | "CANCELLED"
): "default" | "secondary" | "success" | "warning" | "destructive" | "info" {
  return invoiceStatusColors[status];
}

/**
 * Get job status color (legacy compatibility)
 */
export function getJobStatusColor(
  status: "PENDING" | "IN_PROGRESS" | "ON_HOLD" | "COMPLETED" | "CANCELLED"
): "default" | "secondary" | "success" | "warning" | "destructive" | "info" {
  return jobStatusColors[status];
}

/**
 * Status Color Guidelines:
 *
 * 1. Semantic Meaning:
 *    - Green (success): Positive completion states
 *    - Amber (warning): Attention needed, pending actions
 *    - Red (error/destructive): Negative states, failures
 *    - Blue (info): Informational, in-progress states
 *    - Gray (neutral): Drafts, on-hold, default states
 *
 * 2. Consistency:
 *    - Always use these centralized color mappings
 *    - Never use arbitrary colors for status badges
 *    - Use the same color for semantically similar states across different entities
 *
 * 3. Accessibility:
 *    - All colors meet WCAG AA contrast requirements
 *    - Color is never the only indicator (always include text labels)
 *    - Badge variants handle both light and dark modes
 *
 * 4. Examples:
 *    - Completed job = Green (success)
 *    - Pending quote = Amber (warning)
 *    - Failed email = Red (error)
 *    - In progress job = Blue (info)
 *    - Draft invoice = Gray (neutral)
 */
